import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Pencil, Trash2, Handshake, ExternalLink, Upload, X, Building2, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AdminPageHeader,
  AdminSheet,
  AdminEmptyState,
  AdminTableSkeleton,
  DeleteConfirmDialog,
} from './shared';

interface Partner {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  type: string;
  partner_category: string | null;
  logo_url: string | null;
  website_url: string | null;
  contribution: string | null;
  display_order: number | null;
  is_active: boolean | null;
  is_featured: boolean | null;
  adoption_phase: string | null;
  engagement_type: string | null;
}

import { 
  PARTNER_CATEGORIES, 
  PARTNER_ADOPTION_PHASES, 
  PARTNER_ENGAGEMENT_TYPES,
  ADOPTION_PHASE_COLORS,
  ENGAGEMENT_TYPE_COLORS 
} from '@/lib/admin/constants';

export function PartnersManager() {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [activeTab, setActiveTab] = useState<'founding' | 'contributing'>('founding');

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    type: 'founding',
    partner_category: '',
    logo_url: '',
    website_url: '',
    contribution: '',
    display_order: 0,
    is_active: true,
    is_featured: false,
    adoption_phase: '',
    engagement_type: '',
  });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState<Partner | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter partners by type
  const foundingPartners = partners
    .filter(p => p.type === 'founding')
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  const contributingPartners = partners
    .filter(p => p.type === 'contributing')
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('partners')
      .select('*')
      .order('display_order');

    if (error) {
      toast({ title: 'Error fetching partners', description: error.message, variant: 'destructive' });
    } else if (data) {
      setPartners(data);
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      type: activeTab,
      partner_category: '',
      logo_url: '',
      website_url: '',
      contribution: '',
      display_order: 0,
      is_active: true,
      is_featured: false,
      adoption_phase: '',
      engagement_type: '',
    });
    setEditingPartner(null);
  };

  const openCreateEditor = () => {
    resetForm();
    setEditorOpen(true);
  };

  const openEditEditor = (partner: Partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      slug: partner.slug,
      description: partner.description || '',
      type: partner.type,
      partner_category: partner.partner_category || '',
      logo_url: partner.logo_url || '',
      website_url: partner.website_url || '',
      contribution: partner.contribution || '',
      display_order: partner.display_order || 0,
      is_active: partner.is_active ?? true,
      is_featured: partner.is_featured ?? false,
      adoption_phase: partner.adoption_phase || '',
      engagement_type: partner.engagement_type || '',
    });
    setEditorOpen(true);
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file type', description: 'Please upload an image file', variant: 'destructive' });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum file size is 2MB', variant: 'destructive' });
      return;
    }

    setIsUploading(true);
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `logos/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('partners')
      .upload(filePath, file);

    if (uploadError) {
      toast({ title: 'Upload failed', description: uploadError.message, variant: 'destructive' });
      setIsUploading(false);
      return;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('partners')
      .getPublicUrl(filePath);

    setFormData({ ...formData, logo_url: publicUrl });
    setIsUploading(false);
    toast({ title: 'Logo uploaded successfully' });
  };

  const removeLogo = () => {
    setFormData({ ...formData, logo_url: '' });
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }

    setIsSaving(true);
    const slug = formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

    const payload = {
      name: formData.name,
      slug,
      description: formData.description || null,
      type: formData.type,
      partner_category: formData.partner_category || null,
      logo_url: formData.logo_url || null,
      website_url: formData.website_url || null,
      contribution: formData.contribution || null,
      display_order: formData.display_order,
      is_active: formData.is_active,
      is_featured: formData.type === 'founding',
      adoption_phase: formData.adoption_phase || null,
      engagement_type: formData.engagement_type || null,
    };

    if (editingPartner) {
      const { error } = await supabase
        .from('partners')
        .update(payload)
        .eq('id', editingPartner.id);

      if (error) {
        toast({ title: 'Error updating partner', description: error.message, variant: 'destructive' });
      } else {
        toast({ title: 'Partner updated' });
        setEditorOpen(false);
        fetchData();
      }
    } else {
      const { error } = await supabase
        .from('partners')
        .insert(payload);

      if (error) {
        toast({ title: 'Error creating partner', description: error.message, variant: 'destructive' });
      } else {
        toast({ title: 'Partner created' });
        setEditorOpen(false);
        fetchData();
      }
    }
    setIsSaving(false);
  };

  const confirmDelete = (partner: Partner) => {
    setPartnerToDelete(partner);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!partnerToDelete) return;
    
    setIsDeleting(true);
    const { error } = await supabase.from('partners').delete().eq('id', partnerToDelete.id);
    
    if (error) {
      toast({ title: 'Error deleting partner', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Partner deleted' });
      fetchData();
    }
    
    setIsDeleting(false);
    setDeleteDialogOpen(false);
    setPartnerToDelete(null);
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    
    if (sourceIndex === destIndex) return;

    // Determine which list we're reordering
    const isFounding = result.source.droppableId === 'founding-partners';
    const currentList = isFounding ? [...foundingPartners] : [...contributingPartners];
    
    // Reorder the list
    const [movedItem] = currentList.splice(sourceIndex, 1);
    currentList.splice(destIndex, 0, movedItem);

    // Optimistically update the UI
    const updatedPartners = partners.map(p => {
      const newIndex = currentList.findIndex(item => item.id === p.id);
      if (newIndex !== -1) {
        return { ...p, display_order: newIndex };
      }
      return p;
    });
    setPartners(updatedPartners);

    // Batch update in database
    const updates = currentList.map((partner, index) => ({
      id: partner.id,
      display_order: index,
    }));

    for (const update of updates) {
      const { error } = await supabase
        .from('partners')
        .update({ display_order: update.display_order })
        .eq('id', update.id);

      if (error) {
        toast({ title: 'Error updating order', description: error.message, variant: 'destructive' });
        fetchData(); // Revert on error
        return;
      }
    }

    toast({ title: 'Order updated' });
  };

  const getAdoptionPhaseLabel = (phase: string | null) => {
    return PARTNER_ADOPTION_PHASES.find(p => p.value === phase)?.label || phase;
  };

  const getEngagementTypeLabel = (type: string | null) => {
    return PARTNER_ENGAGEMENT_TYPES.find(t => t.value === type)?.label || type;
  };

  const renderPartnerTable = (partnersList: Partner[], droppableId: string) => (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={droppableId}>
        {(provided) => (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="w-16">Logo</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Engagement</TableHead>
                  <TableHead className="w-20">Status</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                {partnersList.map((partner, index) => (
                  <Draggable key={partner.id} draggableId={partner.id} index={index}>
                    {(provided, snapshot) => (
                      <TableRow
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`cursor-pointer ${snapshot.isDragging ? 'bg-muted shadow-lg' : ''}`}
                        onClick={() => openEditEditor(partner)}
                      >
                        <TableCell 
                          {...provided.dragHandleProps}
                          onClick={(e) => e.stopPropagation()}
                          className="cursor-grab active:cursor-grabbing"
                        >
                          <GripVertical className="h-4 w-4 text-muted-foreground" />
                        </TableCell>
                        <TableCell>
                          {partner.logo_url ? (
                            <img 
                              src={partner.logo_url} 
                              alt={partner.name} 
                              className="w-10 h-10 object-contain rounded-md bg-muted"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center">
                              <Building2 className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{partner.name}</div>
                          {partner.website_url && (
                            <a 
                              href={partner.website_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Website <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {partner.adoption_phase && (
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${ADOPTION_PHASE_COLORS[partner.adoption_phase] || ''}`}
                              >
                                {getAdoptionPhaseLabel(partner.adoption_phase)}
                              </Badge>
                            )}
                            {partner.engagement_type && (
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${ENGAGEMENT_TYPE_COLORS[partner.engagement_type] || ''}`}
                              >
                                {getEngagementTypeLabel(partner.engagement_type)}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={partner.is_active ? 'default' : 'secondary'}>
                            {partner.is_active ? 'Active' : 'Draft'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div 
                            className="flex items-center justify-end gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditEditor(partner)}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => confirmDelete(partner)}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            </Table>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Partners"
        count={partners.length}
        onAdd={openCreateEditor}
        addLabel="Add Partner"
      />

      {loading ? (
        <AdminTableSkeleton columns={6} rows={4} />
      ) : partners.length === 0 ? (
        <AdminEmptyState
          icon={Handshake}
          title="No partners yet"
          description="Add partners to showcase on the public partners page."
          actionLabel="Add Partner"
          onAction={openCreateEditor}
        />
      ) : (
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'founding' | 'contributing')}>
          <TabsList>
            <TabsTrigger value="founding">
              Founding Partners ({foundingPartners.length})
            </TabsTrigger>
            <TabsTrigger value="contributing">
              Contributing Organizations ({contributingPartners.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="founding">
            {foundingPartners.length === 0 ? (
              <AdminEmptyState
                icon={Handshake}
                title="No founding partners yet"
                description="Add founding partners to showcase on the public partners page."
                actionLabel="Add Founding Partner"
                onAction={openCreateEditor}
              />
            ) : (
              renderPartnerTable(foundingPartners, 'founding-partners')
            )}
          </TabsContent>
          
          <TabsContent value="contributing">
            {contributingPartners.length === 0 ? (
              <AdminEmptyState
                icon={Handshake}
                title="No contributing organizations yet"
                description="Add contributing organizations to showcase on the public partners page."
                actionLabel="Add Contributing Organization"
                onAction={openCreateEditor}
              />
            ) : (
              renderPartnerTable(contributingPartners, 'contributing-partners')
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Editor Sheet */}
      <AdminSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        title={editingPartner ? 'Edit Partner' : (activeTab === 'founding' ? 'Add Founding Partner' : 'Add Contributing Organization')}
        description={editingPartner ? 'Update the partner details.' : 'Add a new partner to showcase.'}
        onSave={handleSave}
        isSaving={isSaving}
        saveLabel={editingPartner ? 'Update' : 'Create'}
        size="xl"
      >
        <div className="space-y-6">
          {/* Logo Upload */}
          <div className="space-y-2">
            <Label>Logo</Label>
            {formData.logo_url ? (
              <div className="flex items-center gap-4">
                <img 
                  src={formData.logo_url} 
                  alt="Partner logo" 
                  className="w-20 h-20 object-contain rounded-lg bg-muted border p-2"
                />
                <Button variant="outline" size="sm" onClick={removeLogo}>
                  <X className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button 
                  variant="outline" 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {isUploading ? 'Uploading...' : 'Upload Logo'}
                </Button>
                <p className="text-xs text-muted-foreground mt-1">Max 2MB, PNG or JPG</p>
              </div>
            )}
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., CDK Global"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={formData.slug}
                placeholder="auto-generated if empty"
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="partner_category">Category</Label>
            <Select
              value={formData.partner_category}
              onValueChange={(value) => setFormData({ ...formData, partner_category: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {PARTNER_CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* New Engagement Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Adoption Phase</Label>
              <Select
                value={formData.adoption_phase || 'none'}
                onValueChange={(value) => setFormData({ 
                  ...formData, 
                  adoption_phase: value === 'none' ? '' : value 
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select phase..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not Applicable</SelectItem>
                  {PARTNER_ADOPTION_PHASES.map(phase => (
                    <SelectItem key={phase.value} value={phase.value}>
                      {phase.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                For partners developing with the standard
              </p>
            </div>

            <div className="space-y-2">
              <Label>Engagement Type</Label>
              <Select
                value={formData.engagement_type || 'none'}
                onValueChange={(value) => setFormData({ 
                  ...formData, 
                  engagement_type: value === 'none' ? '' : value 
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not Set</SelectItem>
                  {PARTNER_ENGAGEMENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                How they support the initiative
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="website_url">Website URL</Label>
            <Input
              id="website_url"
              value={formData.website_url}
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
              placeholder="https://example.com"
              type="url"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this partner..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contribution">Contribution</Label>
            <Textarea
              id="contribution"
              value={formData.contribution}
              onChange={(e) => setFormData({ ...formData, contribution: e.target.value })}
              placeholder="What has this partner contributed to the standards?"
              rows={2}
            />
          </div>
          
          <div className="flex items-center gap-3">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <Label htmlFor="is_active">Active</Label>
          </div>
        </div>
      </AdminSheet>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        itemName={partnerToDelete?.name}
        itemType="partner"
        isDeleting={isDeleting}
      />
    </div>
  );
}
